import { buildSearchQuery } from './respecto-search-query.js';
import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}


const CMM_REOPEN_KEY = 'respecto:cmm-reopen';

const buildCmmPrefixSearchQuery = buildSearchQuery;

function initRespectoMobileMenu(drawer) {
  if (!(drawer instanceof HTMLElement) || drawer.dataset.cmmExternalReady === 'true') return;
  drawer.dataset.cmmExternalReady = 'true';

  const updateViewportHeight = () => {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    drawer.style.setProperty('--cmm-viewport-height', `${Math.round(viewportHeight)}px`);
  };

  updateViewportHeight();
  window.visualViewport?.addEventListener('resize', updateViewportHeight);
  window.addEventListener('orientationchange', updateViewportHeight);

  const benefits = drawer.querySelector('.cmm-benefits');
  if (benefits) {
    const animationClass = 'cmm-benefits--animated';
    const drawerDetails = drawer.closest('details');

    if ('IntersectionObserver' in window) {
      const benefitsObserver = new IntersectionObserver(
        ([entry]) => {
          benefits.classList.toggle(animationClass, entry.isIntersecting);
        },
        { root: drawer, threshold: 0.35 }
      );
      benefitsObserver.observe(benefits);
    } else {
      benefits.classList.add(animationClass);
    }

    drawerDetails?.addEventListener('toggle', () => {
      if (!drawerDetails.open) benefits.classList.remove(animationClass);
    });
  }

  let reopenState = null;
  try {
    reopenState = JSON.parse(sessionStorage.getItem(CMM_REOPEN_KEY) || 'null');
    sessionStorage.removeItem(CMM_REOPEN_KEY);
  } catch {
    reopenState = null;
  }

  if (reopenState) {
    const headerDrawer = drawer.closest('header-drawer');
    const details = headerDrawer?.querySelector('.menu-drawer-container');
    const summary = details?.querySelector('summary');

    if (headerDrawer && details && summary) {
      details.classList.add('cmm-language-restoring');
      details.setAttribute('open', '');
      details.classList.add('menu-open');
      summary.setAttribute('aria-expanded', 'true');
      drawer.dataset.cmmRestored = 'true';
      drawer.scrollTop = Number(reopenState.scrollTop) || 0;

      customElements.whenDefined('header-drawer').then(() => {
        headerDrawer.open();
        window.setTimeout(() => details.classList.remove('cmm-language-restoring'), 220);
      });
    }
  }

  const language = drawer.querySelector('[data-cmm-language]');
  const languageTrigger = language?.querySelector('.cmm-lang-trigger');
  const languageMenu = language?.querySelector('.cmm-lang-dropdown');

  const setLanguageOpen = (open) => {
    if (!languageTrigger || !languageMenu) return;
    languageTrigger.setAttribute('aria-expanded', String(open));
    languageMenu.setAttribute('aria-hidden', String(!open));
  };

  languageTrigger?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    setLanguageOpen(languageTrigger.getAttribute('aria-expanded') !== 'true');
  });

  language?.querySelectorAll('[data-cmm-language-form]').forEach((option) => {
    option.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const form = document.getElementById(option.dataset.cmmLanguageForm);
      if (!form) return;

      try {
        sessionStorage.setItem(CMM_REOPEN_KEY, JSON.stringify({ scrollTop: drawer.scrollTop }));
      } catch {
        // Localization must still work when storage is unavailable.
      }

      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    });
  });

  document.addEventListener('click', (event) => {
    if (language && !language.contains(event.target)) setLanguageOpen(false);
  });

  language?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setLanguageOpen(false);
      languageTrigger?.focus();
    }
  });

  const search = drawer.querySelector('[data-cmm-search]');
  const input = search?.querySelector('.cmm-search-input');
  const clearButton = search?.querySelector('.cmm-search-clear');
  const results = search?.querySelector('.cmm-predictive');
  let debounceTimer;
  let requestController;

  const closeResults = () => {
    if (!input || !results) return;
    results.dataset.open = 'false';
    input.setAttribute('aria-expanded', 'false');
    results.removeAttribute('aria-busy');
  };

  const updateClearButton = () => {
    if (clearButton && input) clearButton.hidden = input.value.length === 0;
  };

  const runSearch = async () => {
    if (!input || !results) return;
    const query = input.value.trim();

    if (query.length < 3) {
      requestController?.abort();
      results.innerHTML = '';
      closeResults();
      return;
    }

    requestController?.abort();
    const requestedQuery = query;
    const controller = new AbortController();
    requestController = controller;
    results.dataset.open = 'true';
    input.setAttribute('aria-expanded', 'true');
    results.setAttribute('aria-busy', 'true');
    results.innerHTML = `<p class="cmm-predictive__loading">${drawer.dataset.cmmSearchLoading || 'Searching…'}</p>`;

    const url = new URL(drawer.dataset.cmmSearchUrl || '/search', window.location.origin);
    url.searchParams.set('q', buildCmmPrefixSearchQuery(query));
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', 'cmm-search-results');

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`Search request failed: ${response.status}`);

      const html = await response.text();
      if (controller.signal.aborted || input.value.trim() !== requestedQuery) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('[data-cmm-search-results]');
      results.innerHTML =
        content?.innerHTML ||
        `<p class="cmm-predictive__empty">${drawer.dataset.cmmSearchEmpty || 'No products found'}</p>`;
      results.removeAttribute('aria-busy');
    } catch (error) {
      if (
        error.name === 'AbortError' ||
        controller.signal.aborted ||
        input.value.trim() !== requestedQuery
      ) return;
      results.innerHTML = `<p class="cmm-predictive__empty">${drawer.dataset.cmmSearchError || 'Search is temporarily unavailable'}</p>`;
      results.removeAttribute('aria-busy');
    }
  };

  input?.addEventListener('focus', () => {
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    requestAnimationFrame(() => input.scrollIntoView({ block: 'nearest', behavior }));
  });

  input?.addEventListener('input', () => {
    updateClearButton();
    clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(runSearch, 280);
  });

  input?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeResults();
      input.select();
    }
  });

  clearButton?.addEventListener('click', () => {
    if (!input || !results) return;
    input.value = '';
    updateClearButton();
    results.innerHTML = '';
    closeResults();
    input.focus();
  });

  document.addEventListener('click', (event) => {
    if (search && !search.contains(event.target)) closeResults();
  });

  updateClearButton();
}

function initAllRespectoMobileMenus(root = document) {
  root.querySelectorAll?.('[data-cmm-drawer]').forEach(initRespectoMobileMenu);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAllRespectoMobileMenus(), { once: true });
} else {
  initAllRespectoMobileMenus();
}

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches('[data-cmm-drawer]')) initRespectoMobileMenu(node);
      initAllRespectoMobileMenus(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });


